import {
  BURN_MAX_SECONDS,
  BURN_TICK_DAMAGE,
  BURN_TICK_SECONDS,
  FRACTURE_VULNERABILITY,
  HEADSHOT_BAND,
  HEADSHOT_MULTIPLIER,
  SHOCK_SECONDS,
  SHOCK_TICK_SECONDS,
  STAND_HEIGHT,
} from '../constants.ts';
import { rollDice } from '../dice.ts';
import { dist, dist2d, segmentHitsCapsule, type Vec3 } from '../math.ts';
import { segmentHitsBarrier } from '../placed.ts';
import { drainPp, isEmbodied, playerHeight, type PlayerState } from '../player.ts';
import { dealDamage } from './death.ts';
import {
  findPlayer,
  PLAYER_RADIUS,
  type Projectile,
  type SimEvent,
  type SimState,
  type WorldQuery,
} from './state.ts';

/**
 * Projectiles in flight, what they hit, and the riders a hit carries — burn,
 * chill, shock, knockback and the debuffs.
 *
 * Every way a projectile can end reports one `impact` event from one place, so a
 * new way for a spell to end cannot quietly stop making a noise.
 */

/** The effect burning spells leave behind. Not a spell anyone can select. */
export const BURNING_ID = 'burning';
/** The effect Pain leaves behind. Not a spell anyone can select. */
export const SHOCKED_ID = 'shocked';
/**
 * Where each body of a volley starts, in the caster's own right/up plane.
 *
 * A triangle, point upward, because three things in a row read as a mistake and
 * three in a triangle read as a formation. Half a metre apart: wide enough to
 * see three axes leave the hand as three, tight enough that all three still
 * arrive on one mage at the range a Cleric fights at.
 *
 * They travel *parallel* rather than converging. A spread that converges is a
 * shotgun with a magic range at which it is perfect; parallel throws stay a
 * spread at every distance, so landing two of three is a thing that happens
 * because of where somebody was standing rather than how far away they were.
 */
export function volleyPattern(count: number): { x: number; y: number }[] {
  if (count <= 1) return [{ x: 0, y: 0 }];
  if (count === 3) {
    return [
      { x: 0, y: VOLLEY_SPACING * 0.58 },
      { x: -VOLLEY_SPACING * 0.5, y: -VOLLEY_SPACING * 0.29 },
      { x: VOLLEY_SPACING * 0.5, y: -VOLLEY_SPACING * 0.29 },
    ];
  }
  // Anything else: an even fan across the caster's right.
  return Array.from({ length: count }, (_, i) => ({
    x: (i - (count - 1) / 2) * VOLLEY_SPACING,
    y: 0,
  }));
}

/** How far apart the bodies of a volley leave the hand, in metres. */
export const VOLLEY_SPACING = 0.5;

/**
 * A little upward with every shove.
 *
 * Purely horizontal, a knockback slides a mage along the floor and reads as them
 * losing their footing on ice. A touch of lift makes it a blow.
 */
export const KNOCK_LIFT = 2.2;

/** How far a chain will jump, and how much it loses each time. */
export const CHAIN_REACH = 9;
export const CHAIN_FALLOFF = 0.7;

export function tickProjectiles(s: SimState, world: WorldQuery, dt: number, out: SimEvent[]): void {
  for (let i = s.projectiles.length - 1; i >= 0; i--) {
    const pr = s.projectiles[i];
    const step = pr.speed * dt;
    const next = {
      x: pr.pos.x + pr.dir.x * step,
      y: pr.pos.y + pr.dir.y * step,
      z: pr.pos.z + pr.dir.z * step,
    };

    let consumed = false;
    let against: 'player' | 'barrier' | 'level' | 'expired' = 'level';
    let contactPoint: Vec3 | null = null;

    // Player hit first, so you cannot be saved by a wall behind the target.
    for (const target of s.players) {
      if (target.id === pr.ownerId) continue;
      if (!isEmbodied(target)) continue;
      if (target.faction === pr.faction) continue;

      // Against the whole step, not just its end. An Ice Dart covers 0.8m a
      // tick, which is wider than a body, so an endpoint test let the fastest
      // spells in the game pass clean through the people they hit.
      const contact = segmentHitsCapsule(
        pr.pos, next, target.pos, playerHeight(target), PLAYER_RADIUS,
      );
      if (contact) {
        const owner = findPlayer(s, pr.ownerId) ?? null;
        if (pr.radius > 0) {
          applyBlast(s, owner, contact, pr, out);
        } else {
          if (pr.applies === 'debuff') {
            // A debuff deals no damage of its own. Bleeding's dice are the total
            // it will bleed for, spread across its duration.
            applyDebuff(s, target, pr, out);
          } else {
            const head = isHeadshot(target, contact);
            const roll = rollDice(s.rng, pr.dice);
            const damage = head
              ? Math.round(roll * HEADSHOT_MULTIPLIER)
              : roll;
            dealDamage(s, owner, target, damage, pr.element, out);
            if (head) out.push({ type: 'headshot', sourceId: pr.ownerId, targetId: target.id });
            applyOnHit(target, pr);
          }
        }
        consumed = true;
        against = 'player';
        contactPoint = contact;
        break;
      }
    }

    // A wall stops a spell regardless of who raised it. Walling off a corridor
    // walls off your own line down it, and that is the point of the decision.
    if (!consumed) {
      for (let bi = s.barriers.length - 1; bi >= 0; bi--) {
        const b = s.barriers[bi];
        // Dispell is stopped by a wall the way anything else is — and takes the
        // wall with it. Unlike everything else it also crosses one it cannot
        // see through, because unmaking the wall is the whole point of it.
        const dispels = pr.spellId === 'dispell';
        if (!b.blocksSpells && !dispels) continue;
        if (!segmentHitsBarrier(b, pr.pos, next)) continue;

        if (dispels) s.barriers.splice(bi, 1);
        else if (pr.radius > 0) {
          applyBlast(s, findPlayer(s, pr.ownerId) ?? null, next, pr, out);
        }
        consumed = true;
        against = 'barrier';
        contactPoint = next;
        break;
      }
    }

    if (!consumed && world.segmentBlocked(pr.pos, next)) {
      if (pr.radius > 0) {
        applyBlast(s, findPlayer(s, pr.ownerId) ?? null, next, pr, out);
      }
      consumed = true;
      against = 'level';
      contactPoint = next;
    }

    pr.pos = next;
    pr.travelled += step;
    if (!consumed && pr.travelled >= pr.maxRange) {
      consumed = true;
      against = 'expired';
    }

    if (consumed) {
      // Reported from one place rather than from each of the four branches
      // above, so a new way for a spell to end cannot quietly stop making a
      // noise. `hit` says who was wounded; this says where the spell went.
      out.push({
        type: 'impact',
        spellId: pr.spellId,
        element: pr.element,
        pos: contactPoint ?? pr.pos,
        dir: pr.dir,
        against,
        radius: pr.radius,
        tier: pr.tier,
      });
      s.projectiles.splice(i, 1);
    }
  }
}

export function applyBlast(
  s: SimState, owner: PlayerState | null, centre: Vec3, pr: Projectile, out: SimEvent[],
): void {
  const roll = rollDice(s.rng, pr.dice);
  const caught: PlayerState[] = [];

  for (const target of s.players) {
    if (!isEmbodied(target)) continue;
    if (target.faction === pr.faction) continue;
    const d = dist(centre, {
      x: target.pos.x, y: target.pos.y + playerHeight(target) / 2, z: target.pos.z,
    });
    if (d > pr.radius) continue;
    // Linear falloff from centre, never below a third of the roll.
    const scale = Math.max(0.33, 1 - d / pr.radius);
    dealDamage(s, owner, target, Math.round(roll * scale), pr.element, out);
    // Burning and drain do not fall off. Being at the edge of a Fire Ball is
    // still being on fire.
    applyOnHit(target, pr);
    caught.push(target);
  }

  if (pr.chainTargets) applyChain(s, owner, pr, roll, caught, out);
}

/**
 * Jumps from whoever the blast caught to somebody it missed.
 *
 * The chain reaches *past* the explosion rather than widening it, which is what
 * makes it read as lightning rather than as a bigger bang: it starts at a body,
 * finds the nearest enemy that was not already hit, and arcs to them.
 *
 * Each jump is weaker than the last and each is drawn, because a chain nobody
 * can see is indistinguishable from an area spell with a suspiciously large
 * radius — and being able to watch it pick its way through a group is most of
 * why the spell is worth casting.
 */
export function applyChain(
  s: SimState,
  owner: PlayerState | null,
  pr: Projectile,
  roll: number,
  caught: readonly PlayerState[],
  out: SimEvent[],
): void {
  let from = caught[0];
  if (!from) return;

  const hit = new Set(caught.map((p) => p.id));
  let damage = roll;

  for (let jump = 0; jump < (pr.chainTargets ?? 0); jump++) {
    damage = Math.round(damage * CHAIN_FALLOFF);
    if (damage <= 0) return;

    let next: PlayerState | null = null;
    let nearest = CHAIN_REACH;
    for (const other of s.players) {
      if (!isEmbodied(other) || other.faction === pr.faction || hit.has(other.id)) continue;
      const d = dist2d(from!.pos, other.pos);
      if (d >= nearest) continue;
      next = other;
      nearest = d;
    }
    if (!next) return;

    hit.add(next.id);
    dealDamage(s, owner, next, damage, pr.element, out);
    applyOnHit(next, pr);
    out.push({ type: 'arc', sourceId: from!.id, targetId: next.id, element: pr.element });
    from = next;
  }
}

/**
 * Whether a contact landed in the top band of the body.
 *
 * The hitbox is a capsule, so the head is a zone rather than a modelled part.
 * That is honest while mages are capsules, and it is the first thing that will
 * want revisiting once they are meshes with actual proportions — a band measured
 * as a fraction of height will not survive a model whose head is a different
 * share of it. Measured against standing height whether or not the target is
 * crouched, so ducking makes you a smaller target rather than an easier one.
 */
export function isHeadshot(target: PlayerState, contact: Vec3): boolean {
  const height = playerHeight(target);
  const band = STAND_HEIGHT * HEADSHOT_BAND;
  return contact.y >= target.pos.y + height - band;
}

/**
 * Lands a carried status on whoever the projectile struck.
 *
 * Every branch here used to sit inline in the cast, which meant it could only
 * ever be applied by the hitscan that no longer exists.
 */
/**
 * Cold, on hit.
 *
 * Refreshed rather than stacked, and the *deeper* slow wins while both would be
 * running. Stacking would make an Ice Dart into a root by the fourth one — it is
 * a primary, fired constantly, and anything that accumulates from a spammable
 * spell eventually becomes the strongest control in the game by patience alone.
 *
 * Taking the deeper of the two matters when a Cold Ball lands on somebody
 * already chilled by a dart: the ball is the bigger spell and should not be
 * quietly weakened by the smaller one having got there first.
 */
export function applyChill(
  target: PlayerState, chill: number, seconds: number, sourceId: string,
): void {
  const existing = target.effects.find((e) => e.chilled);
  if (existing) {
    existing.slow = Math.min(existing.slow ?? 1, chill);
    existing.remaining = Math.max(existing.remaining, seconds);
    existing.total = Math.max(existing.total ?? seconds, seconds);
    return;
  }

  target.effects.push({
    spellId: 'chilled',
    chilled: true,
    slow: chill,
    remaining: seconds,
    total: seconds,
    sourceId,
  });
}

export function applyDebuff(
  s: SimState, target: PlayerState, pr: Projectile, out: SimEvent[],
): void {
  const duration = pr.duration ?? 6;
  const total = duration;

  switch (pr.spellId) {
    case 'dispell':
      target.effects = target.effects.filter((e) => e.resist === undefined);
      break;

    case 'cramp':
      /*
       * Slowed rather than rooted. Paralysis is the hard stop; this is the one
       * you actually have available when you need it.
       *
       * Eased from 0.5, which read as nearly the same denial as a root for a
       * third of the cost — measured, a half-speed Cramp cost a fleeing mage
       * 8.7m over its three seconds against Paralysis's 9.3m in one and a half.
       * At 0.6 it is clearly the lesser of the two, which is what the price says
       * it should be.
       */
      target.effects.push({
        spellId: pr.spellId, slow: 0.6, remaining: duration, total, sourceId: pr.ownerId,
      });
      break;

    case 'paralysis':
      /*
       * A root is a root: `rooted` is what stops the legs *and* the jump.
       *
       * Movement scale alone left a paralysed mage hopping on the spot, because
       * jump velocity is vertical and `moveScale` only ever gated the horizontal
       * step. Nothing was gained by it — they landed where they took off — but
       * "rooted in place" and "hopping in place" are not the same claim, and the
       * one the spell makes is the first.
       *
       * Crouching stays allowed. It costs speed, which a rooted mage has none of
       * to lose, so denying it would take away the one thing they can still do
       * about incoming fire without giving the caster anything.
       */
      target.effects.push({
        spellId: pr.spellId, slow: 0, rooted: true,
        remaining: duration, total, sourceId: pr.ownerId,
      });
      break;

    case 'fracture':
      // Negative resistance is vulnerability. `resistanceTo` already sums these
      // and `applyDamage` already scales by 1 − total, so a broken bone needs no
      // new machinery: it just makes every other spell in the team hit harder.
      target.effects.push({
        spellId: pr.spellId, resist: -FRACTURE_VULNERABILITY,
        remaining: duration, total, sourceId: pr.ownerId,
      });
      break;

    default: {
      if (pr.dice.count <= 0) break;
      if (pr.ticks) {
        target.effects.push({
          spellId: pr.spellId,
          remaining: duration,
          total,
          sourceId: pr.ownerId,
          tickEvery: 1,
          tickDice: pr.dice,
          // Half an interval in, for the same reason burning is: on the interval
          // the last tick of an n-second bleed falls on the exact frame it
          // expires, and whether it lands at all comes down to floating point.
          nextTick: 0.5,
        });
        break;
      }
      target.effects.push({
        spellId: pr.spellId,
        dps: rollDice(s.rng, pr.dice) / duration,
        remaining: duration,
        total,
        sourceId: pr.ownerId,
      });
      break;
    }
  }
  out.push({ type: 'debuff', sourceId: pr.ownerId, targetId: target.id, spellId: pr.spellId });
}

/** Whatever a spell does beyond its dice: burning, and power drain. */
export function applyOnHit(target: PlayerState, pr: Projectile): void {
  if (pr.burnTicks) applyBurning(target, pr.burnTicks, pr.ownerId);
  if (pr.chill) applyChill(target, pr.chill, pr.chillSeconds ?? 1.5, pr.ownerId);
  if (pr.ppDrain) drainPp(target, pr.ppDrain);

  /*
   * A shove along the flight path, not away from the caster.
   *
   * The difference matters: a mage caught side-on is pushed across their own
   * line of travel rather than directly backwards, which is what makes a Stone
   * Fist worth aiming at a doorway somebody is about to reach instead of at the
   * mage themselves. Core sets the impulse; the controller integrates the arc,
   * the same path a Sign of Expulsion uses.
   */
  if (pr.knockback) {
    target.pendingImpulse = {
      x: pr.dir.x * pr.knockback,
      y: Math.max(0, pr.dir.y) * pr.knockback + KNOCK_LIFT,
      z: pr.dir.z * pr.knockback,
    };
  }
}

/**
 * Sets a target burning, or lengthens a burn already running.
 *
 * A second hit never stacks a second fire — it buys more time on the one that is
 * already going, up to five seconds. That is what keeps Fire a war of attrition
 * rather than a way to multiply damage by holding the trigger.
 */
export function applyBurning(target: PlayerState, ticks: number, sourceId: string): void {
  if (!isEmbodied(target)) return;
  const added = ticks * BURN_TICK_SECONDS;
  const burning = target.effects.find((e) => e.spellId === BURNING_ID);

  if (burning) {
    burning.remaining = Math.min(BURN_MAX_SECONDS, burning.remaining + added);
    burning.total = Math.max(burning.total ?? 0, burning.remaining);
    burning.sourceId = sourceId;
    return;
  }
  target.effects.push({
    spellId: BURNING_ID,
    element: 'fire',
    remaining: Math.min(BURN_MAX_SECONDS, added),
    total: Math.min(BURN_MAX_SECONDS, added),
    sourceId,
    tickEvery: BURN_TICK_SECONDS,
    tickDamage: BURN_TICK_DAMAGE,
    // Half an interval, not a whole one, so ticks land in the middle of each
    // half-second rather than on its edge. On the edge the final tick of an
    // n-tick burn falls on the exact frame the effect expires, and whether it
    // lands at all comes down to floating point — a two-tick burn that deals one
    // damage some of the time.
    nextTick: BURN_TICK_SECONDS / 2,
  });
}

/**
 * Lightning that goes on hurting, refreshed rather than extended.
 *
 * The difference from burning is one line and the whole of the design. A burn
 * *adds* seconds, which rewards a Magician for landing dart after dart; a shock
 * *sets* them, so a second Pain restarts four seconds rather than making eight.
 * Pain is heavy and slow and on a long cooldown, and stacking it would quietly
 * turn the Mentalist into a damage class it is not.
 */
export function applyShock(target: PlayerState, perTick: number, sourceId: string): void {
  const already = target.effects.find((e) => e.spellId === SHOCKED_ID);
  if (already) {
    already.remaining = SHOCK_SECONDS;
    already.total = SHOCK_SECONDS;
    already.tickDamage = Math.max(already.tickDamage ?? 0, perTick);
    already.sourceId = sourceId;
    return;
  }

  target.effects.push({
    spellId: SHOCKED_ID,
    element: 'light',
    remaining: SHOCK_SECONDS,
    total: SHOCK_SECONDS,
    sourceId,
    tickEvery: SHOCK_TICK_SECONDS,
    tickDamage: perTick,
    // Half an interval, for the same reason burning uses one: on the edge the
    // last tick lands on the frame the effect expires and whether it happens at
    // all comes down to floating point.
    nextTick: SHOCK_TICK_SECONDS / 2,
  });
}
